import { Controller } from "@hotwired/stimulus"
import Chart from 'chart.js/auto'

export default class extends Controller {
  static targets = [
    "recordingIndicator",
    "recordToggleIcon",
    "timeRangeSelect",
    "chartCanvas"
  ]

  static values = {
    buildSessionId: String,
    isRecording: { type: Boolean, default: false }, // Changed to false to prevent duplicate monitoring
    timeRange: { type: String, default: "realtime" },
    metrics: { type: Array, default: [] },
    currentMetrics: { type: Object, default: {} }
  }

  connect() {
    this.setupEventListeners()
    this.initializeChart()
    // Don't start our own monitoring - we receive data from preview_devtools_controller
    // this.startMonitoring() // Commented out to prevent duplicate monitoring
  }

  disconnect() {
    this.teardownEventListeners()
    this.stopMonitoring()
    if (this.chart) {
      this.chart.destroy()
    }
  }

  setupEventListeners() {
    // Listen for performance data from the preview-devtools controller
    this.element.addEventListener('preview-devtools:performance-message', this.handlePerformanceMessage.bind(this))
    this.element.addEventListener('preview-devtools:performance-data-received', this.handlePerformanceDataReceived.bind(this))
    this.element.addEventListener('preview-devtools:clear-performance', this.clearMetrics.bind(this))
    this.element.addEventListener('preview-devtools:export-performance', this.exportMetrics.bind(this))
    
    // Also listen on document level for performance data
    document.addEventListener('preview-devtools:performance-data-received', this.handlePerformanceDataReceived.bind(this))
  }

  teardownEventListeners() {
    this.element.removeEventListener('preview-devtools:performance-message', this.handlePerformanceMessage.bind(this))
    this.element.removeEventListener('preview-devtools:performance-data-received', this.handlePerformanceDataReceived.bind(this))
    this.element.removeEventListener('preview-devtools:clear-performance', this.clearMetrics.bind(this))
    this.element.removeEventListener('preview-devtools:export-performance', this.exportMetrics.bind(this))
    document.removeEventListener('preview-devtools:performance-data-received', this.handlePerformanceDataReceived.bind(this))
  }

  handlePerformanceMessage(event) {
    const performanceData = event.detail
    this.updateMetrics(performanceData)
  }

  handlePerformanceDataReceived(event) {
    // Always handle performance data from the preview-devtools controller
    // This data comes from the centralized monitoring, not from our own collection
    const performanceData = event.detail
    this.updateMetrics(performanceData)
    
    // Update recording indicator to show data is being received
    if (!this.isRecordingValue && this.hasRecordingIndicatorTarget) {
      this.recordingIndicatorTarget.classList.add('bg-green-500')
      this.recordingIndicatorTarget.classList.remove('bg-red-500')
    }
  }

  startMonitoring() {
    if (this.monitoringInterval) return
    
    this.monitoringInterval = setInterval(() => {
      if (this.isRecordingValue) {
        this.collectMetrics()
      }
    }, 1000) // Collect every second
    
    // Start animation loop for FPS monitoring
    this.startFPSMonitoring()
  }

  stopMonitoring() {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval)
      this.monitoringInterval = null
    }
    
    if (this.fpsAnimationFrame) {
      cancelAnimationFrame(this.fpsAnimationFrame)
      this.fpsAnimationFrame = null
    }
  }

  startFPSMonitoring() {
    let lastTime = performance.now()
    let frames = 0
    let fps = 0
    
    const measureFPS = (currentTime) => {
      frames++
      
      if (currentTime >= lastTime + 1000) {
        fps = Math.round((frames * 1000) / (currentTime - lastTime))
        frames = 0
        lastTime = currentTime
        
        this.currentMetricsValue = {
          ...this.currentMetricsValue,
          fps: fps
        }
        
        this.updateDisplay()
      }
      
      if (this.isRecordingValue) {
        this.fpsAnimationFrame = requestAnimationFrame(measureFPS)
      }
    }
    
    this.fpsAnimationFrame = requestAnimationFrame(measureFPS)
  }

  collectMetrics() {
    const iframe = document.querySelector('#preview-iframe')
    if (!iframe || !iframe.contentWindow) return
    
    // Request performance metrics from iframe
    iframe.contentWindow.postMessage({
      type: 'collect-performance'
    }, '*')
  }

  updateMetrics(data) {
    const timestamp = new Date()
    const metrics = {
      timestamp: timestamp,
      ...data
    }
    
    // Add to metrics array
    this.metricsValue = [...this.metricsValue, metrics]
    
    // Limit based on time range
    this.trimMetrics()
    
    // Update current metrics
    this.currentMetricsValue = {
      ...this.currentMetricsValue,
      ...data
    }
    
    // Update display
    this.updateDisplay()
    
    // Update chart
    this.updateChart()
  }

  trimMetrics() {
    const now = new Date()
    let cutoffTime
    
    switch (this.timeRangeValue) {
      case '1m':
        cutoffTime = new Date(now - 60 * 1000)
        break
      case '5m':
        cutoffTime = new Date(now - 5 * 60 * 1000)
        break
      case '10m':
        cutoffTime = new Date(now - 10 * 60 * 1000)
        break
      default: // realtime - keep last 30 seconds
        cutoffTime = new Date(now - 30 * 1000)
    }
    
    this.metricsValue = this.metricsValue.filter(m => m.timestamp > cutoffTime)
  }

  updateDisplay() {
    // Update metric cards with current values
    const metrics = this.currentMetricsValue
    
    // Update FPS
    const fpsElement = this.element.querySelector('[data-metric="fps"]')
    if (fpsElement) {
      fpsElement.textContent = metrics.fps || '-'
      fpsElement.className = this.getFPSClass(metrics.fps)
    }
    
    // Update Memory
    const memoryElement = this.element.querySelector('[data-metric="memory"]')
    if (memoryElement) {
      memoryElement.textContent = this.formatMemory(metrics.memory_used)
    }
    
    // Update DOM Nodes
    const domNodesElement = this.element.querySelector('[data-metric="dom_nodes"]')
    if (domNodesElement) {
      domNodesElement.textContent = metrics.dom_nodes || '-'
    }
    
    // Update Event Listeners
    const eventListenersElement = this.element.querySelector('[data-metric="event_listeners"]')
    if (eventListenersElement) {
      eventListenersElement.textContent = metrics.event_listeners || '-'
    }
  }

  getFPSClass(fps) {
    if (!fps) return 'text-base-content'
    if (fps >= 55) return 'text-success'
    if (fps >= 30) return 'text-warning'
    return 'text-error'
  }

  formatMemory(bytes) {
    if (!bytes) return '-'
    const mb = bytes / (1024 * 1024)
    return `${mb.toFixed(2)} MB`
  }

  initializeChart() {
    if (!this.hasChartCanvasTarget) return
    
    const ctx = this.chartCanvasTarget.getContext('2d')
    
    this.chart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: [],
        datasets: [
          {
            label: 'FPS',
            data: [],
            borderColor: 'rgb(34, 197, 94)',
            backgroundColor: 'rgba(34, 197, 94, 0.1)',
            yAxisID: 'y-fps'
          },
          {
            label: 'Memory (MB)',
            data: [],
            borderColor: 'rgb(59, 130, 246)',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            yAxisID: 'y-memory'
          },
          {
            label: 'DOM Nodes',
            data: [],
            borderColor: 'rgb(251, 146, 60)',
            backgroundColor: 'rgba(251, 146, 60, 0.1)',
            yAxisID: 'y-nodes',
            hidden: true
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false
        },
        plugins: {
          legend: {
            position: 'top',
            labels: {
              boxWidth: 12,
              font: {
                size: 10
              }
            }
          }
        },
        scales: {
          x: {
            display: true,
            ticks: {
              font: {
                size: 10
              }
            }
          },
          'y-fps': {
            type: 'linear',
            display: true,
            position: 'left',
            min: 0,
            max: 60,
            ticks: {
              font: {
                size: 10
              }
            }
          },
          'y-memory': {
            type: 'linear',
            display: true,
            position: 'right',
            ticks: {
              font: {
                size: 10
              }
            },
            grid: {
              drawOnChartArea: false
            }
          },
          'y-nodes': {
            type: 'linear',
            display: false
          }
        }
      }
    })
  }

  updateChart() {
    if (!this.chart) return
    
    const labels = this.metricsValue.map(m => {
      const date = new Date(m.timestamp)
      return date.toLocaleTimeString('en-US', { 
        hour12: false,
        minute: '2-digit',
        second: '2-digit'
      })
    })
    
    const fpsData = this.metricsValue.map(m => m.fps || 0)
    const memoryData = this.metricsValue.map(m => (m.memory_used || 0) / (1024 * 1024))
    const nodesData = this.metricsValue.map(m => m.dom_nodes || 0)
    
    this.chart.data.labels = labels
    this.chart.data.datasets[0].data = fpsData
    this.chart.data.datasets[1].data = memoryData
    this.chart.data.datasets[2].data = nodesData
    
    this.chart.update('none') // No animation for performance
  }

  toggleRecording() {
    this.isRecordingValue = !this.isRecordingValue
    
    if (this.isRecordingValue) {
      this.recordingIndicatorTarget.classList.add('animate-pulse')
      this.recordToggleIconTarget.classList.remove('fa-play')
      this.recordToggleIconTarget.classList.add('fa-pause')
      this.startFPSMonitoring()
    } else {
      this.recordingIndicatorTarget.classList.remove('animate-pulse')
      this.recordToggleIconTarget.classList.remove('fa-pause')
      this.recordToggleIconTarget.classList.add('fa-play')
      if (this.fpsAnimationFrame) {
        cancelAnimationFrame(this.fpsAnimationFrame)
        this.fpsAnimationFrame = null
      }
    }
  }

  changeTimeRange(event) {
    this.timeRangeValue = event.target.value
    this.trimMetrics()
    this.updateChart()
  }

  clearMetrics() {
    this.metricsValue = []
    this.currentMetricsValue = {}
    this.updateDisplay()
    this.updateChart()
  }

  exportMetrics() {
    const data = {
      metrics: this.metricsValue,
      currentMetrics: this.currentMetricsValue,
      timeRange: this.timeRangeValue,
      exportedAt: new Date().toISOString()
    }
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `performance-metrics-${Date.now()}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }
}